import React, { FC, useState } from 'react';
import { ReactComponent as CloseIcon } from '../../../icons/close-modal.svg';
import { ReactComponent as PlusAddIcon } from '../../../icons/plus-add.svg';
import { ReactComponent as ActionEmailSendIcon } from '../../../icons/action-email-send.svg';
import { ReactComponent as ActionDraftEmailIcon } from '../../../icons/action-draft-email.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../icons/chevron-left.svg';

import '../../chat/sidebar/add-todo-modal.scss';
import './onboarding-task-modal.scss';

export interface OnboardingTaskModalPill {
  label: string;
  value: string;
  options?: string[];
  onSelect?: (value: string) => void;
}

interface OnboardingTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateTask: () => void;
  title: string;
  taskDescription: string;
  pills: OnboardingTaskModalPill[];
  actionLabel: string;
  actionType: 'email_from_assistant' | 'draft_email_from_user';
  submitting?: boolean;
}

const OnboardingTaskModal: FC<OnboardingTaskModalProps> = ({
  isOpen,
  onClose,
  onCreateTask,
  title,
  taskDescription,
  pills,
  actionLabel,
  actionType,
  submitting = false,
}) => {
  const [activePillLabel, setActivePillLabel] = useState<string | null>(null);

  if (!isOpen) return null;

  const ActionIcon =
    actionType === 'email_from_assistant'
      ? ActionEmailSendIcon
      : ActionDraftEmailIcon;

  return (
    <div className='onboarding-task-modal-overlay' onClick={onClose}>
      <div
        className='add-todo-modal-container'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='add-todo-modal'>
          <div className='modal-header'>
            <h1 className='modal-title'>{title}</h1>
          </div>
          <div className='modal-body'>
            <div className='todo-input-wrapper'>
              <div className='todo-input' style={{ cursor: 'default' }}>
                {taskDescription}
              </div>
            </div>

            <div className='questions-and-actions-row'>
              {pills.length > 0 && (
                <div className='question-pills-row'>
                  {activePillLabel ? (
                    <>
                      <button
                        className='pill-back-btn'
                        onClick={() => setActivePillLabel(null)}
                      >
                        <ChevronLeftIcon />
                      </button>
                      {(
                        pills.find((p) => p.label === activePillLabel)
                          ?.options || []
                      ).map((opt) => (
                        <button
                          key={opt}
                          className='question-pill option'
                          onClick={() => {
                            const pill = pills.find(
                              (p) => p.label === activePillLabel,
                            );
                            pill?.onSelect?.(opt);
                            setActivePillLabel(null);
                          }}
                        >
                          {opt}
                        </button>
                      ))}
                    </>
                  ) : (
                    pills.map((pill) => (
                      <span
                        key={pill.label}
                        className={`question-pill answered${pill.options && pill.options.length > 1 ? '' : ''}`}
                        style={
                          pill.options && pill.options.length > 1
                            ? { cursor: 'pointer' }
                            : undefined
                        }
                        onClick={
                          pill.options && pill.options.length > 1
                            ? () => setActivePillLabel(pill.label)
                            : undefined
                        }
                      >
                        {pill.label}: {pill.value}
                      </span>
                    ))
                  )}
                </div>
              )}

              <div className='action-bar'>
                <div className='action-buttons-left' />
                <div className='action-create-group'>
                  <div className='dropdown-wrapper'>
                    <div className='action-trigger'>
                      <ActionIcon />
                      {actionLabel}
                    </div>
                  </div>
                  <button
                    className='create-btn'
                    onClick={onCreateTask}
                    disabled={submitting}
                  >
                    <PlusAddIcon />
                    {submitting ? 'Creating…' : 'Create Task'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button className='close-btn' onClick={onClose}>
          <CloseIcon />
        </button>
      </div>
    </div>
  );
};

export default OnboardingTaskModal;
